import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Building2, ExternalLink } from 'lucide-react';

// Image URLs
const IMAGES = {
  magazineCover: "https://customer-assets.emergentagent.com/job_wagner-profile/artifacts/c6a64o6y_Gemini_Generated_Image_zhkbzizhkbzizhkb.png",
  crystalAward: "https://customer-assets.emergentagent.com/job_wagner-profile/artifacts/lgs66uw9_Gemini_Generated_Image_sz0vd4sz0vd4sz0v.png",
  bridgeHandshake: "https://customer-assets.emergentagent.com/job_wagner-profile/artifacts/acyob2mr_Gemini_Generated_Image_kspm8ykspm8ykspm.png",
  leadershipAwards: "https://customer-assets.emergentagent.com/job_wagner-profile/artifacts/v2a3qn3p_Gemini_Generated_Image_dy2fnhdy2fnhdy2f.png",
  summitPresentation: "https://customer-assets.emergentagent.com/job_wagner-profile/artifacts/i4vu97ud_Gemini_Generated_Image_cqtchrcqtchrcqtc.png"
};

// Article Header Component
const ArticleHeader = ({ title, source, year, readTime, heroImage, altText }) => (
  <div className="article-header">
    <div className="article-hero">
      <img src={heroImage} alt={altText || title} className="article-hero-image" />
      <div className="article-hero-overlay"></div>
    </div>
    <div className="container">
      <Link to="/#publications" className="article-back-link">
        <ArrowLeft size={18} />
        <span>Back to Publications</span>
      </Link>
      <div className="article-meta-bar">
        <span className="article-source">{source}</span>
        <div className="article-meta-details">
          <span><Calendar size={14} /> {year}</span>
          <span><Clock size={14} /> {readTime} min read</span>
        </div>
      </div>
      <h1 className="article-title">{title}</h1>
    </div>
  </div>
);

// Article Section Component
const ArticleSection = ({ title, children }) => (
  <div className="article-section">
    <h2 className="article-section-title">{title}</h2>
    {children}
  </div>
);

// Quote Block Component
const QuoteBlock = ({ quote, attribution }) => (
  <blockquote className="article-quote">
    <p>"{quote}"</p>
    {attribution && <cite>— {attribution}</cite>}
  </blockquote>
);

// Related Projects Component
const RelatedProjects = ({ projects }) => (
  <div className="article-related">
    <h3>Related Projects</h3>
    <div className="related-grid">
      {projects.map((project, index) => (
        <div key={index} className="related-card">
          <Building2 size={20} />
          <div>
            <strong>{project.name}</strong>
            <span>{project.role}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// ARTICLE 1: ENR - Cross-Continental Infrastructure
// ============================================================
export const ENRArticle = () => {
  const relatedProjects = [
    { name: "Eastgate Rapid Transit Hub", role: "Executive Strategy Lead" },
    { name: "Northwind River Bridge", role: "Strategic Oversight" },
    { name: "Continental Rail Network", role: "Cross-Border Coordination" }
  ];

  return (
    <article className="article-page">
      <ArticleHeader
        title="The Future of Cross-Continental Infrastructure Networks"
        source="Engineering News-Record"
        year="2023"
        readTime="8"
        heroImage={IMAGES.summitPresentation}
        altText="Ruben P. Wargner delivering keynote presentation at Global Infrastructure Summit"
      />
      
      <div className="container">
        <div className="article-content">
          <p className="article-lead">
            In an era of unprecedented global connectivity, the infrastructure sector stands at a pivotal crossroads. 
            Ruben P. Wargner, Founder and Chief Strategy Officer of Redspire Civil Engineering, shares his insights 
            on the emerging trends reshaping how nations approach cross-border infrastructure development.
          </p>

          <ArticleSection title="Overview">
            <p>
              The infrastructure landscape is undergoing a fundamental transformation. As economies become increasingly 
              interconnected, the demand for seamless cross-continental networks has never been greater. From high-speed 
              rail corridors spanning multiple nations to unified digital infrastructure systems, the challenges and 
              opportunities facing today's engineering leaders require a new paradigm of thinking.
            </p>
            <p>
              With over three decades of experience directing infrastructure programs across Europe and North America, 
              Wargner brings a unique perspective to these challenges—one forged through hands-on leadership of projects 
              that have bridged not just physical distances, but regulatory frameworks, engineering philosophies, and 
              cultural approaches to construction.
            </p>
          </ArticleSection>

          <div className="article-image-block">
            <img src={IMAGES.bridgeHandshake} alt="Ruben P. Wargner leading strategic partnership meeting for cross-border infrastructure collaboration" />
            <span className="image-caption">Cross-border collaboration on major infrastructure projects requires navigating complex stakeholder relationships</span>
          </div>

          <ArticleSection title="Key Insights">
            <p>
              <strong>The Convergence of Standards:</strong> One of the most significant developments in cross-continental 
              infrastructure is the gradual harmonization of engineering standards. "We're seeing a real evolution in how 
              regulatory bodies approach mutual recognition," Wargner explains. "Projects that once required duplicate 
              certifications can now proceed with streamlined compliance frameworks."
            </p>
            <p>
              <strong>Digital Twin Integration:</strong> The adoption of digital twin technology has revolutionized how 
              multinational projects are planned and executed. Real-time modeling allows teams across different time zones 
              to collaborate on design iterations, predict maintenance needs, and optimize lifecycle costs before ground 
              is even broken.
            </p>
            <p>
              <strong>Climate Resilience Imperative:</strong> Cross-continental networks face unique challenges from climate 
              variability. Infrastructure spanning multiple climate zones must account for diverse environmental stressors, 
              from permafrost degradation in northern corridors to increased flooding risks in coastal regions.
            </p>
          </ArticleSection>

          <QuoteBlock 
            quote="Strategic clarity, disciplined governance, and transparent communication are the foundations of every successful infrastructure program. When you're coordinating across borders, these principles become even more critical."
            attribution="Ruben P. Wargner"
          />

          <ArticleSection title="Ruben's Commentary">
            <p>
              "The most successful cross-continental projects I've been involved with share a common characteristic: 
              they prioritize relationship-building as much as engineering excellence," Wargner reflects. "Technical 
              specifications can be harmonized through negotiation, but trust must be earned through consistent delivery."
            </p>
            <p>
              He emphasizes the importance of what he calls "generational thinking"—designing infrastructure with 
              100-year horizons rather than short-term political cycles. "When you're building networks that will serve 
              multiple generations across multiple nations, you can't afford to cut corners. Every decision must consider 
              long-term maintenance, adaptability to technological change, and resilience against unforeseen challenges."
            </p>
            <p>
              Wargner also addresses the role of public-private partnerships in funding cross-continental projects. 
              "The scale of investment required for these networks exceeds what any single government can typically 
              commit. Structuring financing that aligns private returns with public benefit is essential—and it requires 
              sophisticated governance frameworks that protect all stakeholders."
            </p>
          </ArticleSection>

          <ArticleSection title="Impact on Infrastructure Leadership">
            <p>
              The evolution of cross-continental infrastructure is reshaping what it means to be an engineering leader. 
              Technical expertise remains foundational, but today's infrastructure executives must also be skilled diplomats, 
              financial strategists, and change managers.
            </p>
            <p>
              "I tell young engineers that the future belongs to those who can think systemically," Wargner advises. 
              "Understanding how your bridge connects to a rail network, which feeds into a port system, which serves 
              global supply chains—that holistic view is what separates good engineers from transformative leaders."
            </p>
            <p>
              As nations continue to invest in infrastructure as a driver of economic growth and strategic positioning, 
              the demand for leaders who can navigate this complexity will only intensify. The blueprint Wargner and 
              others are developing today will shape how infrastructure connects our world for decades to come.
            </p>
          </ArticleSection>

          <RelatedProjects projects={relatedProjects} />
        </div>
      </div>
    </article>
  );
};

// ============================================================
// ARTICLE 2: ASCE - Generational Thinking
// ============================================================
export const ASCEArticle = () => {
  const relatedProjects = [
    { name: "Vltava River Bridge Modernization", role: "Lead Structural Modeler" },
    { name: "Sundale Flood Resilience Upgrade", role: "Risk & Resilience Advisor" },
    { name: "Harbor District Redevelopment", role: "Lifecycle Design Lead" }
  ];

  return (
    <article className="article-page">
      <ArticleHeader
        title="Generational Thinking in Civil Engineering"
        source="ASCE Civil Engineering Magazine"
        year="2022"
        readTime="10"
        heroImage={IMAGES.crystalAward}
        altText="Ruben P. Wargner receiving crystal engineering excellence award for lifetime achievement"
      />
      
      <div className="container">
        <div className="article-content">
          <p className="article-lead">
            What does it mean to build for generations yet unborn? Ruben P. Wargner explores the philosophy and 
            practice of long-horizon infrastructure planning—an approach that has defined his career and shaped 
            how Redspire Civil Engineering approaches every project.
          </p>

          <ArticleSection title="Overview">
            <p>
              The Roman aqueducts still standing today weren't designed with quarterly earnings in mind. The engineers 
              who built them understood that true infrastructure serves not just the present generation, but many 
              generations to come. Somewhere along the way, modern engineering lost sight of this fundamental truth.
            </p>
            <p>
              "Generational thinking" is Ruben Wargner's term for reclaiming that long-view perspective—designing and 
              building infrastructure with explicit consideration for how it will perform, adapt, and serve communities 
              over a 100-year horizon or longer. It's an approach that runs counter to much of contemporary practice, 
              but one that Wargner argues is essential for addressing the infrastructure challenges of the 21st century.
            </p>
          </ArticleSection>

          <div className="article-image-block">
            <img src={IMAGES.bridgeHandshake} alt="Ruben P. Wargner with engineering team discussing long-term infrastructure planning and generational thinking" />
            <span className="image-caption">Generational infrastructure requires collaboration across disciplines and decades</span>
          </div>

          <ArticleSection title="Key Insights">
            <p>
              <strong>Beyond Design Life:</strong> Standard engineering practice typically specifies a design life of 
              50-75 years for major structures. Generational thinking challenges engineers to consider what happens 
              after that—not just demolition and replacement, but adaptation, retrofit potential, and eventual 
              deconstruction impact.
            </p>
            <p>
              <strong>Material Selection for Longevity:</strong> Short-term cost optimization often drives material 
              choices that create long-term maintenance burdens. Wargner advocates for lifecycle cost analysis that 
              includes projected maintenance over 100+ years, even when initial capital costs are higher.
            </p>
            <p>
              <strong>Adaptive Capacity:</strong> Perhaps the most challenging aspect of generational design is 
              building in flexibility for uses and technologies that don't yet exist. "The bridges we build today 
              may need to accommodate vehicle types not yet invented," Wargner notes. "Our designs must anticipate 
              change we cannot fully predict."
            </p>
          </ArticleSection>

          <QuoteBlock 
            quote="We don't just build structures—we engineer legacies. Every decision we make today will be judged by engineers and communities 100 years from now. That responsibility should humble us and inspire us."
            attribution="Ruben P. Wargner"
          />

          <ArticleSection title="Ruben's Commentary">
            <p>
              "My thinking on this topic was shaped by my early career in Prague," Wargner reflects. "I worked on 
              bridges over the Vltava River that had been standing for centuries—structures built by engineers who 
              knew their work would outlast them by generations. That sense of responsibility is something I try to 
              instill in every project Redspire undertakes."
            </p>
            <p>
              He acknowledges the practical challenges of generational thinking in a world driven by short-term 
              financial metrics. "Clients don't always want to pay for 100-year durability when they're focused on 
              next quarter's budget. Part of our role as engineers is to advocate for the long view—to help decision-makers 
              understand that infrastructure investments are intergenerational contracts."
            </p>
            <p>
              Wargner's approach extends beyond technical specifications to governance structures. "We design maintenance 
              protocols and knowledge transfer systems as part of our deliverables. The best-designed structure will fail 
              if future stewards don't understand how to care for it."
            </p>
          </ArticleSection>

          <ArticleSection title="Impact on Infrastructure Leadership">
            <p>
              Generational thinking requires a different kind of engineering leadership—one comfortable with uncertainty, 
              committed to documentation and knowledge preservation, and willing to advocate for investments whose benefits 
              may not be realized within current political or corporate cycles.
            </p>
            <p>
              "The engineers who will maintain our structures in 2125 aren't born yet," Wargner observes. "We owe them 
              clear documentation, robust designs, and materials that won't have degraded beyond repair. We owe them 
              infrastructure that can adapt to climate conditions we can only model. Most of all, we owe them the same 
              care and craftsmanship that previous generations of engineers gave to us."
            </p>
            <p>
              As the profession grapples with aging infrastructure worldwide, generational thinking offers a framework 
              for breaking the cycle of build-neglect-replace. It's a philosophy that demands more of engineers—but 
              promises structures worthy of the investment societies make in them.
            </p>
          </ArticleSection>

          <RelatedProjects projects={relatedProjects} />
        </div>
      </div>
    </article>
  );
};

// ============================================================
// ARTICLE 3: Construction Dive - Bridging Cultures
// ============================================================
export const ConstructionDiveArticle = () => {
  const relatedProjects = [
    { name: "Alpine Tunnel System", role: "Innovation Lead" },
    { name: "Metropolitan Transit Corridor", role: "Design Oversight" },
    { name: "Coastal Highway Interchange", role: "Seismic Engineering" }
  ];

  return (
    <article className="article-page">
      <ArticleHeader
        title="Bridging Cultures: European Innovation in American Infrastructure"
        source="Construction Dive"
        year="2021"
        readTime="12"
        heroImage={IMAGES.leadershipAwards}
        altText="Ruben P. Wargner receiving multiple international leadership awards for cross-cultural engineering excellence"
      />
      
      <div className="container">
        <div className="article-content">
          <p className="article-lead">
            When Ruben P. Wargner brought Redspire Civil Engineering from Prague to Salt Lake City, Utah in 2015, 
            he carried more than business plans and client relationships. He brought a European engineering 
            philosophy refined through centuries of building for permanence—and a conviction that American 
            infrastructure could benefit from its integration.
          </p>

          <ArticleSection title="Overview">
            <p>
              The transatlantic divide in civil engineering approaches runs deeper than most practitioners realize. 
              European engineering culture, shaped by geography constrained by existing structures and regulations 
              emphasizing permanence, produces different solutions than the American tradition of building fast and 
              expansively across abundant land.
            </p>
            <p>
              Neither approach is inherently superior—each reflects rational responses to different conditions. But as 
              American infrastructure ages and land constraints tighten, European techniques for rehabilitation, 
              retrofitting, and building within complex existing environments offer valuable lessons. Wargner has 
              spent his career bridging these two traditions, adapting European rigor for American contexts while 
              preserving what makes each approach effective.
            </p>
          </ArticleSection>

          <ArticleSection title="Key Insights">
            <p>
              <strong>Rehabilitation vs. Replacement:</strong> European engineering has developed sophisticated 
              techniques for extending structure life through targeted intervention. "In Prague, demolishing a historic 
              bridge isn't an option," Wargner explains. "You learn to strengthen, adapt, preserve. American practice 
              is catching up as our infrastructure ages, but there's still much to learn from European rehabilitation 
              expertise."
            </p>
            <p>
              <strong>Regulatory Integration:</strong> European Union engineering standards have created frameworks for 
              cross-border project coordination that American practice lacks. "Working across state lines in the U.S. 
              sometimes feels more complicated than crossing national borders in Europe," Wargner observes. "There's an 
              opportunity to adapt EU harmonization approaches for American federalism."
            </p>
            <p>
              <strong>Craft Tradition:</strong> European engineering maintains stronger connections to traditional 
              craftsmanship, particularly in concrete and masonry work. "The quality of finish work I see on European 
              structures reflects a cultural value placed on visible craft. American practice tends toward efficiency 
              over aesthetics—but infrastructure serves communities better when it's beautiful as well as functional."
            </p>
          </ArticleSection>

          <QuoteBlock 
            quote="Bringing Redspire from Prague to the United States taught us the value of global perspectives in local challenges. The best solutions often emerge from synthesizing different engineering traditions."
            attribution="Ruben P. Wargner"
          />

          <ArticleSection title="Ruben's Commentary">
            <p>
              "When I arrived in the U.S., I was struck by the scale of American infrastructure ambition—and also by 
              how much deferred maintenance had accumulated," Wargner recalls. "European engineers would never have 
              let bridges and transit systems deteriorate to the extent common in American cities. But the American 
              approach has strengths too: speed of execution, willingness to innovate, and pragmatic problem-solving 
              that European bureaucracies sometimes lack."
            </p>
            <p>
              He describes his role as that of a translator between engineering cultures. "My European background 
              helps me ask questions American engineers sometimes don't think to ask—about long-term durability, about 
              aesthetic integration, about lifecycle costs. But I've also learned from American colleagues about 
              efficient project delivery and stakeholder management."
            </p>
            <p>
              "The Czech-American hybrid identity shapes how I approach every project," Wargner adds. "I see 
              infrastructure through two cultural lenses simultaneously. Sometimes that creates tension, but more 
              often it generates solutions neither tradition would have reached alone."
            </p>
          </ArticleSection>

          <ArticleSection title="Impact on Infrastructure Leadership">
            <p>
              As global infrastructure investment accelerates and engineering talent becomes increasingly mobile, 
              cross-cultural competence is emerging as a critical leadership skill. Engineers who can navigate different 
              regulatory environments, synthesize diverse technical traditions, and communicate across cultural boundaries 
              will lead the profession's future.
            </p>
            <p>
              "I encourage young engineers to work abroad if they can," Wargner advises. "Even a year in a different 
              engineering culture transforms how you see problems and solutions. The infrastructure challenges facing 
              humanity require our best thinking from all traditions—not siloed national approaches competing for 
              superiority."
            </p>
            <p>
              The bridges Wargner builds connect more than riverbanks; they connect engineering philosophies, creating 
              hybrid approaches stronger than either source tradition. It's a model for how globalized infrastructure 
              practice might evolve—not toward homogenization, but toward creative synthesis.
            </p>
          </ArticleSection>

          <RelatedProjects projects={relatedProjects} />
        </div>
      </div>
    </article>
  );
};

// ============================================================
// ARTICLE 4: Featured Recognition
// ============================================================
export const FeaturedRecognitionArticle = () => {
  return (
    <article className="article-page">
      <ArticleHeader
        title="Featured Recognition: Infrastructure Leaders Review"
        source="Infrastructure Leaders Review"
        year="2023"
        readTime="5"
        heroImage={IMAGES.magazineCover}
        altText="Ruben P. Wargner featured as Global Infrastructure Visionary on Infrastructure Leaders Review cover"
      />
      
      <div className="container">
        <div className="article-content">
          <p className="article-lead">
            Ruben P. Wargner was featured on the cover of Infrastructure Leaders Review as a "Global Infrastructure 
            Visionary" in recognition of his cross-continental leadership and transformative impact on civil 
            engineering practice.
          </p>

          <ArticleSection title="About This Recognition">
            <p>
              Infrastructure Leaders Review selected Ruben P. Wargner for their prestigious cover feature, highlighting 
              his unique position as a Czech-American engineering strategist who has successfully bridged European and 
              American infrastructure practices. The recognition acknowledges not just individual project achievements, 
              but a career dedicated to elevating how the profession approaches long-term infrastructure planning.
            </p>
            <p>
              The cover story explores Wargner's journey from Prague to Salt Lake City, the founding and expansion of 
              Redspire Civil Engineering, and his influential philosophy of "generational thinking" that has reshaped 
              how partner agencies and firms approach infrastructure investment decisions.
            </p>
          </ArticleSection>

          <div className="article-image-block featured-cover">
            <img src={IMAGES.magazineCover} alt="Ruben P. Wargner featured on Infrastructure Leaders Review magazine cover as Global Infrastructure Visionary" />
            <span className="image-caption">Infrastructure Leaders Review – July 2023 Cover Feature</span>
          </div>

          <ArticleSection title="Recognition Highlights">
            <p>
              The feature highlighted several aspects of Wargner's career and leadership approach:
            </p>
            <ul className="article-list">
              <li>
                <strong>Cross-Continental Impact:</strong> Successfully establishing Redspire as a bridge between 
                European engineering excellence and American infrastructure needs
              </li>
              <li>
                <strong>Innovative Frameworks:</strong> Development of predictive maintenance and digital twin 
                methodologies now adopted by multiple public agencies
              </li>
              <li>
                <strong>Safety Leadership:</strong> Consistent track record of reducing incident rates across 
                major infrastructure programs
              </li>
              <li>
                <strong>Thought Leadership:</strong> Influential advocacy for lifecycle-based infrastructure 
                investment and 100-year planning horizons
              </li>
              <li>
                <strong>Mentorship:</strong> Commitment to developing the next generation of engineering leaders 
                through hands-on guidance and knowledge transfer
              </li>
            </ul>
          </ArticleSection>

          <QuoteBlock 
            quote="Shaping the future of civil engineering requires both technical mastery and the vision to see beyond immediate project deliverables. Ruben Wargner exemplifies this combination of expertise and foresight."
            attribution="Infrastructure Leaders Review Editorial Board"
          />

          <ArticleSection title="Alignment with Leadership Mission">
            <p>
              This recognition reflects the core values that have guided Wargner's career and Redspire's mission: 
              disciplined governance, transparent communication, and unwavering commitment to infrastructure that 
              serves communities for generations.
            </p>
            <p>
              "Recognition like this belongs to the entire Redspire team and to the partners who have trusted us 
              with their most important infrastructure challenges," Wargner noted in response to the feature. 
              "What we've accomplished reflects collective effort across two continents and multiple decades."
            </p>
            <p>
              The cover feature has been widely shared within the civil engineering community and has helped elevate 
              conversations about long-term infrastructure planning, cross-cultural engineering collaboration, and 
              the evolving role of digital technology in construction and maintenance.
            </p>
          </ArticleSection>

          <div className="article-cta-section">
            <h3>Connect with Ruben P. Wargner</h3>
            <p>
              For speaking engagements, consulting inquiries, or strategic partnership discussions, 
              please reach out through the contact information below.
            </p>
            <Link to="/#contact" className="btn-primary">
              Get in Touch
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
};

export default { ENRArticle, ASCEArticle, ConstructionDiveArticle, FeaturedRecognitionArticle };
